import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import reportWebVitals from "./reportWebVitals";
import InventorySearch from "./components/InventorySearch/InventorySearch";
import { InventoryDetails } from "./components/InventorySearch/InventoryDetails";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import UserAuthPage from "./common/components/userAuth/UserAuthPage";
import ResetPasswordPage from "./common/components/userAuth/ResetPasswordPage";
import SignOut from "./common/components/userAuth/SignOut";
import WelcomePage from "./components/WelcomePage";
import TrackedLinkPage from "./components/TrackedLinkPage";
import logo from "./assets/img/kono-logo.svg";
import PortalContainer from "./components/PortalContainer";
import { SearchHistory } from "./components/SearchHistory";
import { Cart } from "./components/Cart";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import MyOrders, { OrderDetail } from "./components/MyOrders";
import MyInformation from "./components/MyInformation";
import MyQuotes, { QuoteDetail } from "./quotes/MyQuotes";
import MyPurchaseOrders, { PurchaseOrderDetail } from "./quotes/PurchaseOrders";
import RfqWorkspace from "./rfq/RfqWorkspace";
import { TopBarWrapper } from "./components/InventorySearch/TopBar";
import { RootContainer } from "./components/RootContainer";
import { PostHogProvider } from "posthog-js/react";
import { BookmarkedItems } from "./components/BookmarkedItems";
import { UnknownPathRedirect } from "./components/UnknownPathRedirect";
import {
  AboutPage,
  FaqPage,
  PrivacyPage,
  TermsPage,
} from "./components/StaticPages";

const postHogOptions = {
  api_host: process.env.REACT_APP_PUBLIC_POSTHOG_HOST,
  defaults: "2025-11-30",
  capture_pageview: false,
} as const;

const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY as string,
);

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement,
);

const router = createBrowserRouter([
  {
    path: "/",
    element: <RootContainer />,
    children: [
      {
        path: "signup",
        element: <UserAuthPage logoImg={logo} />,
      },
      {
        path: "signin",
        element: <UserAuthPage logoImg={logo} />,
      },
      {
        path: "forgot-password",
        element: <UserAuthPage logoImg={logo} />,
      },
      {
        path: "reset-password",
        element: <ResetPasswordPage logoImg={logo} />,
      },
      {
        path: "signout",
        element: <SignOut />,
      },
      {
        // Buyer-campaign link from the Industry Graph: pre-filled sign-up.
        path: "welcome/:token",
        element: <WelcomePage logoImg={logo} />,
      },
      {
        // A BDR's personal "see it on kono.ai" link: records the visit, then redirects.
        path: "w/:token",
        element: <TrackedLinkPage />,
      },
      {
        path: "*",
        element: <UnknownPathRedirect />,
      },
      {
        path: "/",
        element: <PortalContainer />,
        children: [
          {
            path: "/",
            element: <InventorySearch />,
          },
          {
            path: "product",
            element: (
              <TopBarWrapper>
                <InventoryDetails />
              </TopBarWrapper>
            ),
          },
          {
            path: "orders",
            element: (
              <TopBarWrapper>
                <Elements stripe={stripePromise}>
                  <MyOrders />
                </Elements>
              </TopBarWrapper>
            ),
          },
          {
            path: "orders/:id",
            element: (
              <TopBarWrapper>
                <Elements stripe={stripePromise}>
                  <OrderDetail />
                </Elements>
              </TopBarWrapper>
            ),
          },
          {
            path: "cart",
            element: (
              <TopBarWrapper>
                <Elements stripe={stripePromise}>
                  <Cart />
                </Elements>
              </TopBarWrapper>
            ),
          },
          {
            path: "bookmarked",
            element: (
              <TopBarWrapper>
                <BookmarkedItems embedded={false} />
              </TopBarWrapper>
            ),
          },
          {
            path: "searches",
            element: <SearchHistory />,
          },
          {
            path: "my-information",
            element: (
              <TopBarWrapper>
                <MyInformation />
              </TopBarWrapper>
            ),
          },
          {
            path: "rfq",
            element: (
              <TopBarWrapper>
                <RfqWorkspace />
              </TopBarWrapper>
            ),
          },
          {
            path: "quotes",
            element: (
              <TopBarWrapper>
                <MyQuotes />
              </TopBarWrapper>
            ),
          },
          {
            path: "quotes/:id",
            element: (
              <TopBarWrapper>
                <QuoteDetail />
              </TopBarWrapper>
            ),
          },
          {
            path: "purchase-orders",
            element: (
              <TopBarWrapper>
                <MyPurchaseOrders />
              </TopBarWrapper>
            ),
          },
          {
            path: "purchase-orders/:id",
            element: (
              <TopBarWrapper>
                <Elements stripe={stripePromise}>
                  <PurchaseOrderDetail />
                </Elements>
              </TopBarWrapper>
            ),
          },
          {
            path: "about",
            element: (
              <TopBarWrapper>
                <AboutPage />
              </TopBarWrapper>
            ),
          },
          {
            path: "faq",
            element: (
              <TopBarWrapper>
                <FaqPage />
              </TopBarWrapper>
            ),
          },
          {
            path: "privacy",
            element: (
              <TopBarWrapper>
                <PrivacyPage />
              </TopBarWrapper>
            ),
          },
          {
            path: "terms",
            element: (
              <TopBarWrapper>
                <TermsPage />
              </TopBarWrapper>
            ),
          },
        ],
      },
    ],
  },
]);

root.render(
  <React.StrictMode>
    <PostHogProvider
      apiKey={process.env.REACT_APP_PUBLIC_POSTHOG_KEY || ""}
      options={postHogOptions}
    >
      <RouterProvider router={router} />
    </PostHogProvider>
  </React.StrictMode>,
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
